// App.jsx is the main entry point of the application. It is responsible for
import React, { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RouterProvider } from 'react-router-dom'
import { router } from './router/router'
import { validateToken } from './api/tokenValidation'
import { Auth } from 'aws-amplify'
//import userflow from 'userflow.js'
import { LicenseManager } from 'ag-grid-enterprise'
import { useSessionTimer } from './hooks/useSessionTimer'
import { SystemMessagesProvider } from './context/SystemMessagesContext'
import { ProcessingPollingProvider } from './context/ProcessingPollingContext'
import { ToastProvider } from './context/ToastContext'
import { ToastOutlet } from './components/ui/ScoopToast/ToastOutlet'
import { ThemeProvider } from './context/ThemeContext'
import { initializeThemeHandler, cleanupThemeHandler } from './utils/themeHandler'
import './GTM.js' // <-- Google tag Script
import './amplitud.js' // <-- Amplitude tag Script
import './heap.js' // <-- Heap tag Script
import { StyledEngineProvider } from '@mui/material'

//const USERFLOW_TOKEN = 'ct_4abdvu4v7vfvzlt6rn4crgdbx4'

const App = () => {
    useSessionTimer()
    const dispatch = useDispatch()
    const userID = useSelector((state) => state.auth.userID)
    const userName = useSelector((state) => state.auth.userName)
    const workspaceID = useSelector((state) => state.auth.workspaceID)
    const workspaceName = useSelector((state) => state.auth.workspaceName)
    const scoopUserEmail = useSelector((state) => state.auth.userEmail)
    const identityAlreadySet = useRef(false)
    LicenseManager.setLicenseKey(
        'Using_this_{AG_Grid}_Enterprise_key_{AG-054218}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Scoop_Analytics,_Inc}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{Scoop_Analytics}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{Scoop_Analytics}_need_to_be_licensed___{Scoop_Analytics}_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{25_February_2025}____[v3]_[01]_MTc0MDQ0MTYwMDAwMA==1552ac22a0d8105022c71a9bce2b8bba'
    )

    useEffect(() => {
        dispatch({ type: 'AUTH_STARTED' })
        const queryParams = new URLSearchParams(window.location.search)
        const inviteCode = queryParams.get('invite')
        const workspaceID = queryParams.get('workspaceID')
        if (inviteCode && workspaceID) {
            dispatch({
                type: 'SET_GUEST_MODE',
                isGuestMode: true,
                passcode: inviteCode,
            })
            dispatch({
                type: 'SWITCH_WORKSPACE',
                workspaceID: workspaceID,
                workspaceName: 'Guest',
            })
            dispatch({
                type: 'SET_AUTHENTICATED',
                isAuthenticated: true,
                user: {
                    signInUserSession: {
                        idToken: { jwtToken: inviteCode },
                        refreshToken: { token: inviteCode }, // Empty refreshToken token
                    },
                    username: 'Guest',
                    userFirstName: 'Guest',
                    attributes: {
                        email: null,
                    },
                },
            })
            dispatch({ type: 'AUTH_FINISHED' })
            return // This stops the execution of the rest of the useEffect block
        }
        Auth.currentAuthenticatedUser()
            .then((user) => {
                // Dispatch the authenticated user's information to the Redux store
                dispatch({
                    type: 'SET_AUTHENTICATED',
                    isAuthenticated: true,
                    user: {
                        signInUserSession: {
                            idToken: {
                                jwtToken: user.signInUserSession.idToken.jwtToken,
                            },
                            refreshToken: {
                                token: user.signInUserSession.refreshToken.token,
                            },
                        },
                        username: user.username,
                        userFirstName: user.attributes.given_name, // Assuming the given name attribute is set
                        attributes: {
                            email: user.attributes.email,
                        },
                    },
                })
                dispatch({ type: 'AUTH_FINISHED' })
                // record user login in db
                const body = {
                    action: 'login',
                    userID: user.username,
                    isDev: process.env.REACT_APP_SCOOP_ENV === 'dev',
                }
                fetch(
                    'https://pig8gecvvk.execute-api.us-west-2.amazonaws.com/corsair/ui_information',
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}`,
                        },
                        body: JSON.stringify(body),
                    }
                )
                    .then((response) => {
                        if (!response.ok) {
                            throw new Error('Network response was not ok')
                        }
                        return response.json() // Or handle the response in the way you need if you care about it
                    })
                    .catch((error) => {
                        console.error('Error recording login', error)
                    })
            })
            .catch((err) => {
                const storedToken = localStorage.getItem('userToken')
                if (storedToken) {
                    const isTokenValid = validateToken(storedToken)
                    if (isTokenValid) {
                        dispatch({
                            type: 'SET_AUTHENTICATED',
                            isAuthenticated: true,
                            user: {
                                signInUserSession: {
                                    idToken: { jwtToken: storedToken },
                                    refreshToken: {
                                        token: localStorage.getItem('refreshToken'),
                                    },
                                },
                                username: localStorage.getItem('userID'),
                                userFirstName: localStorage.getItem('userFirstName'),
                                attributes: {
                                    email: localStorage.getItem('userName'),
                                },
                            },
                        })
                        // record user login in db
                        const body = {
                            action: 'login',
                            userID: localStorage.getItem('userID'),
                            isDev: process.env.REACT_APP_SCOOP_ENV === 'dev',
                        }
                        fetch(
                            'https://pig8gecvvk.execute-api.us-west-2.amazonaws.com/corsair/ui_information',
                            {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                    Authorization: `Bearer ${storedToken}`,
                                },
                                body: JSON.stringify(body),
                            }
                        )
                            .then((response) => {
                                if (!response.ok) {
                                    throw new Error('Network response was not ok')
                                }
                                return response.json() // Or handle the response in the way you need if you care about it
                            })
                            .catch((error) => {
                                console.error('Error recording login', error)
                            })
                    } else {
                        dispatch({
                            type: 'LOGOUT',
                        })
                    }
                }
                dispatch({ type: 'AUTH_FINISHED' })
            })
    }, [dispatch])

    // Initialize theme handler for white-label theming via postMessage
    useEffect(() => {
        initializeThemeHandler()

        return () => {
            cleanupThemeHandler()
        }
    }, [])

    // useEffect(() => {
    //     if (userID && workspaceID && userName && workspaceName) {
    //         try {
    //             // Format the date as ISO 8601 in the PST time zone
    //             const pstTime = new Date().toLocaleString('en-US', {
    //                 timeZone: 'America/Los_Angeles',
    //                 hour12: false,
    //             })
    //             const isoDate = new Date(pstTime).toISOString() + '+8'
    //
    //             userflow.init(USERFLOW_TOKEN)
    //             userflow.identify(userID, {
    //                 name: userName,
    //                 email: userName,
    //                 signed_up_at: isoDate,
    //                 workspaceID: workspaceID,
    //                 workspaceName: workspaceName,
    //             })
    //             userflow.track('workspace_selected', {
    //                 workspaceID: workspaceID,
    //                 workspaceName: workspaceName,
    //             })
    //         } catch (error) {
    //             console.error('Error pushing data to userflow:', error)
    //         }
    //     }
    // }, [userID, workspaceID, workspaceID, workspaceName])

    // this is strictly for heap
    useEffect(() => {
        if (scoopUserEmail && userID && !identityAlreadySet.current) {
            identityAlreadySet.current = true
            try {
                if (window.heap?.identify) {
                    window.heap.identify(`${scoopUserEmail}`)
                    window.clarity?.('identify', `${scoopUserEmail}`)
                }
            } catch (error) {}

            try {
                if (window.amplitude) {
                    window.amplitude.setUserId(userID)
                    const identify = new window.amplitude.Identify().set('email', scoopUserEmail)
                    window.amplitude.identify(identify)
                }
            } catch (error) {
                console.error('Error setting Amplitude user identity:', error)
            }

            try {
                window.dataLayer?.push({
                    event: 'email_verified',
                    enhanced_conversion_data: {
                        email: scoopUserEmail,
                        userScoopID: userID,
                    },
                })
                window.dataLayer?.push({
                    event: 'userScoopID',
                    enhanced_conversion_data_userId: {
                        userScoopID: userID,
                    },
                })
            } catch (error) {
                console.error('Error pushing data to dataLayer:', error)
            }
        }
    }, [scoopUserEmail, userID])

    return (
        <ThemeProvider>
            <ToastProvider>
                <SystemMessagesProvider>
                    <ProcessingPollingProvider>
                        <StyledEngineProvider injectFirst>
                            <RouterProvider router={router} />
                        </StyledEngineProvider>
                    </ProcessingPollingProvider>
                </SystemMessagesProvider>
                <ToastOutlet />
            </ToastProvider>
        </ThemeProvider>
    )
}

export default App
